/** Transport-agnostic realtime contract. A feature joins a logical room and
 *  publishes/subscribes without knowing whether the backend is Momento or a
 *  Cloudflare Durable Object. The single implementation lives in
 *  ./do-transport.ts and is shared by web, desktop, and mobile. */

/** Per-frame metadata the transport derives from the wire envelope. */
export interface FrameMeta {
  /** True when another client relayed this frame through the room. The
   *  realtime worker stamps `srvFrom` (the sender's userId) on every
   *  client-relayed frame; server broadcasts never carry it. Server-authored
   *  signals (e.g. `*.room.updated`) must ignore peer frames — a peer could
   *  otherwise forge them. */
  fromPeer: boolean;
  /** Sender userId for peer frames (from `srvFrom`). */
  srvFrom?: string;
}

export interface RealtimeChannel {
  publish(event: unknown): void;
  /** Subscribe to messages. Returns an unsubscribe fn. */
  on(handler: (event: unknown, meta: FrameMeta) => void): () => void;
  /**
   * Subscribe to reconnects — fires after the socket re-opens following a
   * drop (NOT on first connect). Events broadcast while the socket was down
   * are lost, so consumers should refetch mirrored state here. Returns an
   * unsubscribe fn. Optional: not every transport supports it.
   */
  onReconnect?(handler: () => void): () => void;
  /**
   * Subscribe to supersession — the server closed this socket because a newer
   * connection for the same user took over (close code 4001; game rooms allow
   * one socket per user). Terminal: the channel will NOT reconnect, publishes
   * are dropped. Consumers should fall back (e.g. to polling) and may join a
   * fresh channel later on an explicit user signal — never automatically, or
   * two tabs kick each other forever. Fires immediately if already superseded.
   * Returns an unsubscribe fn. Optional: not every transport supports it.
   */
  onSuperseded?(handler: () => void): () => void;
  /** True once the channel was superseded (see {@link onSuperseded}). */
  isSuperseded?(): boolean;
  /**
   * Subscribe to socket lifecycle changes (see {@link ChannelStatus}). Fires
   * on every transition, starting with the next one (not replayed). Returns an
   * unsubscribe fn. Optional: not every transport supports it.
   */
  onStatus?(handler: (status: ChannelStatus) => void): () => void;
  /** Current presence set (userIds), if the transport tracks it. */
  presence(): ReadonlySet<string>;
  close(): void;
}

/**
 * Socket lifecycle as seen by one channel:
 * - `connecting` — fetching a token / opening a socket (first time or after a drop)
 * - `open` — the socket is open; publishes go straight out
 * - `down` — the socket dropped (or the token fetch failed); a reconnect is scheduled
 * - `closed` — the server closed with one of the channel's `terminalCloseCodes`; no reconnect
 * - `superseded` — close code 4001 (see {@link RealtimeChannel.onSuperseded}); no reconnect
 */
export type ChannelStatus =
  | { state: "connecting" }
  | { state: "open" }
  | { state: "down"; code: number | null }
  | { state: "closed"; code: number }
  | { state: "superseded" };

/** Per-channel options for {@link RealtimeTransport.join}. All optional; defaults keep today's behaviour. */
export interface JoinOptions {
  /**
   * Buffer publishes made while the socket is down and flush them on the next
   * open (default true, capped at 50). Protocols that need a handshake frame
   * first (Card Table v2 `hello`) pass false: publishes while down are dropped
   * and the consumer re-sends what it needs from its `open` status handler.
   */
  queueWhileDown?: boolean;
  /** Server close codes after which the channel stops reconnecting (4001 is always terminal). */
  terminalCloseCodes?: readonly number[];
  /**
   * Liveness probe for rooms whose Durable Object auto-answers the text frame `"ping"` with
   * `"pong"` (TableDO / RoomDO `setWebSocketAutoResponse`, free: the DO never wakes). When set,
   * a socket that received nothing for this many ms gets a `"ping"`; if nothing at all arrives
   * within {@link HEARTBEAT_TIMEOUT_MS} after it, the socket is treated as dead (a half-open TCP
   * connection after a network switch never fires `close` on its own) and the channel reconnects.
   * The browser `online` event also probes a socket that is still believed open (with the short
   * {@link ONLINE_PROBE_TIMEOUT_MS}: the network just came back, so a live socket answers fast), and
   * the `offline` event probes it too (a socket that stays silent through the outage is dropped).
   * Default: off — none of these listeners/probes exist on channels that don't opt in.
   */
  heartbeatMs?: number;
}

/** How long a heartbeat `"ping"` (or an `online` probe) may go unanswered before the socket is dropped. */
export const HEARTBEAT_TIMEOUT_MS = 10_000;
/**
 * How long the probe sent when the browser's `online` event fires may go unanswered. The network
 * just returned, so a socket that survived answers within a round trip; a silently dead one
 * (half-open across the outage) is caught this fast instead of after HEARTBEAT_TIMEOUT_MS.
 */
export const ONLINE_PROBE_TIMEOUT_MS = 2_500;

export interface RealtimeTransport {
  join(room: string, opts?: JoinOptions): RealtimeChannel;
}
